import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CONSENT_EVENT, getConsent, setConsent } from "@/lib/consent";

/**
 * Equal-weight choice: declining is as easy as accepting. Reopen via footer "Cookie settings".
 * Mobile: floats above the bottom dock. Desktop: compact card in the bottom-left corner.
 */
export const CookieConsent = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (getConsent() === null) setOpen(true);
    const reopen = () => setOpen(true);
    const close = () => setOpen(false);
    window.addEventListener("swiftix:open-consent", reopen);
    window.addEventListener(CONSENT_EVENT, close);
    return () => {
      window.removeEventListener("swiftix:open-consent", reopen);
      window.removeEventListener(CONSENT_EVENT, close);
    };
  }, []);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-body"
      className="fixed z-[90] inset-x-3 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] lg:inset-x-auto lg:left-6 lg:bottom-6 lg:w-[400px] animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div className="relative overflow-hidden rounded-2xl border border-accent/30 bg-card shadow-2xl">
        <div aria-hidden className="h-1 w-full bg-gradient-to-r from-accent/0 via-accent to-accent/0" />
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-accent/10 text-accent">
              <Cookie className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] text-accent/90">Before the lights go down</p>
              <h2 id="cookie-title" className="mt-0.5 text-sm font-semibold text-foreground">Your cookie choice</h2>
            </div>
          </div>
          <p id="cookie-body" className="mt-3 text-xs leading-relaxed text-muted-foreground">
            We use essential storage to keep you signed in and remember your settings. If you allow it, we also record
            masked session replays to find and fix bugs. No advertising trackers, ever.
          </p>
          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-accent" aria-hidden />
            Change this any time from the footer.{" "}
            <Link to="/cookies" className="underline underline-offset-2 hover:text-foreground">Cookie Policy</Link>
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" className="w-full border-accent/30 hover:border-accent/60 hover:bg-accent/10 hover:text-accent-foreground" onClick={() => setConsent("essential")}>
              Essential only
            </Button>
            <Button variant="outline" size="sm" className="w-full border-accent/30 hover:border-accent/60 hover:bg-accent/10 hover:text-accent-foreground" onClick={() => setConsent("all")}>
              Allow all
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const openCookieSettings = () => window.dispatchEvent(new Event("swiftix:open-consent"));
